/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/orders/refund
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyReverseDeliveryKey,
  ShopifyReverseDeliveryLineItemKey,
  ShopifyReverseDeliveryLineItemToReverseDeliveryKey,
  ShopifyReverseDeliveryToReverseDeliveryLineItemsKey,
  ShopifyReverseDeliveryToReverseFulfillmentOrderKey,
  ShopifyReverseDeliveryToShopKey,
  ShopifyReverseFulfillmentOrderKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyReverseDeliveryKey,
  "2024-07": {
    name: "Shopify Reverse Delivery",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Deliverable",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "deliverable",
            graphQLSelection: `
            ... on ReverseDeliveryShippingDeliverable {
              label {
                createdAt
                publicFileUrl
                updatedAt
              }
              tracking {
                carrierName
                number
                url
              }
            }
          `,
          },
        },
      },
      {
        name: "Reverse Delivery Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyReverseDeliveryToReverseDeliveryLineItemsKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "reverse_delivery_line_items",
            graphQLFieldKey: "reverseDeliveryLineItems",
          },
        },
        configuration: {
          relatedModelKey: ShopifyReverseDeliveryLineItemKey,
          inverseFieldKey: ShopifyReverseDeliveryLineItemToReverseDeliveryKey,
        },
      },
      {
        name: "Reverse Fulfillment Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReverseDeliveryToReverseFulfillmentOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "reverse_fulfillment_order_id",
            graphQLFieldKey: "reverseFulfillmentOrderId",
            skipGraphQLSelection: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyReverseFulfillmentOrderKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReverseDeliveryToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "return.reverseFulfillmentOrders",
      resource: "reverseFulfillmentOrder.reverseDeliveries",
      additionalFields: ["reverseFulfillmentOrderId:id"],
    },
  },
};

export const ShopifyReverseDelivery = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
